import {
  BMC_CORE, BMC_CRITERIA, BMC_LABEL, BMC_REST, TRIADE_PARTS, bmcMetrics, triadePiece, withBmcNormalized, withTriadeSynthesis,
  type Bmc as BmcContent, type BmcListBlock, type BmcSuggestionDto, type Triade, type TriadePart,
} from '@ads/shared';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import * as Ic from '../components/icons';
import { Actions, useViewMeta } from '../components/Shell';
import { useBmcSuggest, useConsolidate, useToolReading } from '../lib/queries';
import { useToolDraft } from '../lib/useToolDraft';
import { BMC_ASK, BMC_ICON, BmcBlockCard, ItemChips, RelationsProvider } from './BmcParts';
import { ReadingStatus } from './GapsDashboard';

/* ─────────────────────────────────────────────────────────────────────
   Business Model Canvas, em três etapas.
   1 · Módulos centrais: a Tríade de Valor (a mesma da ferramenta 03, e
       editável daqui), a proposta de valor, os segmentos e as estratégias
       de relacionamento — ligados entre si.
   2 · Demais módulos: a IA sugere canais, receitas, recursos, atividades,
       parcerias e custos, já com as relações; tudo editável.
   3 · Consolidação: o canvas montado e a leitura da IA — consistência,
       critérios, leituras e ajustes.
   Em todas, passar o mouse num item acende os itens ligados a ele.
   ───────────────────────────────────────────────────────────────────── */

type Step = 1 | 2 | 3;
const cap = (x: string) => (x ? x.charAt(0).toUpperCase() + x.slice(1) : x);
const STEPS: Array<[Step, string]> = [[1, 'Módulos centrais'], [2, 'Demais módulos'], [3, 'Consolidação']];
const TRI_ICON: Record<TriadePart, (p: object) => ReactNode> = { believe: Ic.Heart, through: Ic.Gear, result: Ic.Bars };

export function Bmc() {
  useViewMeta({ nav: 'strategy', crumb: ['Strategy', 'Business Model Canvas'], j: .43 });
  const nav = useNavigate();
  const bmc = useToolDraft('bmc');
  const tri = useToolDraft('triade');
  const q = useToolReading('bmc');
  const suggest = useBmcSuggest();
  const consolidate = useConsolidate();
  const [step, setStep] = useState<Step | null>(null);
  const [proposal, setProposal] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [asking, setAsking] = useState<BmcListBlock[] | 'all' | null>(null);
  const auto = useRef(false);

  const c = bmc.local;
  const t = tri.local;
  const reading = q.data?.reading ?? null;
  const updating = !!q.data && (q.data.pending || q.data.stale);

  /* a etapa inicial: onde o canvas parou */
  useEffect(() => {
    if (!c || step) return;
    const core = !!c.valueProp.trim() && BMC_CORE.every(b => c[b].some(i => i.text.trim()));
    const rest = BMC_REST.every(b => c[b].some(i => i.text.trim()));
    setStep(!core ? 1 : !rest ? 2 : 3);
  }, [c, step]);

  const setCanvas = (next: BmcContent) => bmc.commit(withBmcNormalized(next));
  const flush = () => { bmc.flush(); tri.flush(); };
  const go = (s: Step) => { flush(); setStep(s); document.querySelector('.stage')?.scrollTo({ top: 0, behavior: 'smooth' }); };

  /* sugestões: a proposta de valor, ou itens para os blocos (entram marcados como da IA) */
  const askProposal = () => suggest.mutate({ mode: 'proposta' }, {
    onSuccess: r => { setProposal(r.proposta || null); setNote(r.note); },
  });
  const askBlocks = (blocks: BmcListBlock[] | 'all') => {
    if (!c) return;
    setAsking(blocks);
    suggest.mutate({ mode: 'modulos', blocks: blocks === 'all' ? [] : blocks }, {
      onSuccess: r => { setNote(r.note); merge(r); },
      onSettled: () => setAsking(null),
    });
  };
  const merge = (r: BmcSuggestionDto) => {
    const cur = bmc.local;
    if (!cur) return;
    const next = { ...cur } as BmcContent;
    for (const [b, xs] of Object.entries(r.blocks) as Array<[BmcListBlock, NonNullable<BmcSuggestionDto['blocks'][BmcListBlock]>]>) {
      const have = new Set(next[b].map(i => i.text.trim().toLowerCase()));
      next[b] = [...next[b].filter(i => i.text.trim()), ...xs.filter(x => !have.has(x.text.trim().toLowerCase())).map(x => ({ ...x, ai: true }))];
    }
    setCanvas(next);
  };
  /* ao chegar na etapa 2 com os seis blocos vazios, a IA já traz as sugestões */
  useEffect(() => {
    if (step !== 2 || !c || auto.current) return;
    auto.current = true;
    if (BMC_REST.every(b => !c[b].some(i => i.text.trim())) && c.segments.some(s => s.text.trim())) askBlocks('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, c]);

  const patchTri = (p: Partial<Triade>) => { if (t) tri.commit(withTriadeSynthesis({ ...t, ...p })); };
  const saving = [bmc.saveState, tri.saveState].includes('saving') ? 'saving' : bmc.saveState === 'error' || tri.saveState === 'error' ? 'error' : bmc.saveState === 'saved' || tri.saveState === 'saved' ? 'saved' : 'idle';
  const status = saving === 'saving' ? 'Salvando…' : saving === 'saved' ? 'Salvo' : saving === 'error' ? 'Não salvou — tentando de novo' : '';

  return (
    <section className="view on" data-v="bmc">
      <button className="back" type="button" onClick={() => { flush(); nav('/strategy'); }}><Ic.ChevronLeft />Voltar para Strategy</button>
      <div className="vhead">
        <div>
          <div className="eyebrow"><span className="rule" /><span>Strategy</span><b>Ferramenta 06 / 10</b></div>
          <h1>Business Model Canvas</h1>
          <p className="lede">{step === 2 ? 'Preencha os componentes que sustentam o modelo de negócio.'
            : step === 3 ? 'Revise a arquitetura final do modelo de negócio.'
            : 'Estruture o modelo de negócio com foco em proposta de valor, clientes e relacionamento.'}</p>
        </div>
        <div className="tools">
          {status && <span className={`g-save ${saving}`} aria-live="polite">{saving === 'saved' && <Ic.Check />}{status}</span>}
        </div>
      </div>

      <div className="bmc-top">
        <ol className="stepper" aria-label="Etapas do Business Model Canvas">
          {STEPS.map(([n, label]) => (
            <li key={n} className={step === n ? 'on' : step && n < step ? 'done' : ''}>
              <button type="button" onClick={() => go(n)} aria-current={step === n ? 'step' : undefined}>
                <span className="n">{step && n < step ? <Ic.Check /> : n}</span>{label}
              </button>
            </li>
          ))}
        </ol>
        <div className="bmc-align" title={reading?.resumo}>
          <span>Alinhamento ao propósito</span>
          <span className={`vi-bar${updating ? ' is-dim' : ''}`}><i style={{ '--v': `${reading?.alinhamento ?? 0}%` } as CSSProperties} /></span>
          <b>{reading ? `${reading.alinhamento}%` : '—'}</b>
        </div>
      </div>

      {!c || !t || !step ? <div className="loading-line" /> : (
        <RelationsProvider canvas={c}>
          {step === 1 && (
            <div className="bmc-s1">
              <div className="bmc-col">
                <section className="card bmc-tri">
                  <header className="bmc-card-h">
                    <span className="bmc-ic"><Ic.Triad /></span>
                    <div><h3>Tríade de Valor</h3><p>A essência da proposta de valor — a mesma da ferramenta 03; o que mudar aqui muda lá.</p></div>
                  </header>
                  <div className="bmc-tri-row">
                    {TRIADE_PARTS.map((p, n) => {
                      const I = TRI_ICON[p.key];
                      return (
                        <div key={p.key} className={`bmc-tri-part p-${p.key}`}>
                          <span className="lb"><span className="nn">{n + 1}</span>{p.lead}…<I /></span>
                          <textarea value={t[p.key]} maxLength={240} placeholder={p.placeholder} aria-label={p.lead}
                            onChange={e => patchTri({ [p.key]: e.target.value } as Partial<Triade>)} onBlur={tri.flush} />
                          {n < 2 && <span className="arr" aria-hidden="true"><Ic.ArrowRight /></span>}
                        </div>
                      );
                    })}
                  </div>
                </section>

                <section className="card bmc-vp">
                  <header className="bmc-card-h">
                    <span className="bmc-ic"><Ic.Star /></span>
                    <div><h3>Proposta de Valor</h3><p>{BMC_ASK.valueProp}</p></div>
                    <button type="button" className="btn-ghost sm" onClick={askProposal} disabled={suggest.isPending}>
                      {suggest.isPending && !asking ? <span className="spin" /> : <Ic.Sparkle />}Sugestão da IA</button>
                  </header>
                  <div className="ta bmc-vp-ta">
                    <textarea value={c.valueProp} maxLength={600} rows={3} placeholder="Oferecemos… para… — o que a organização entrega, para quem, e o que muda para eles."
                      aria-label="Proposta de valor" onChange={e => bmc.commit({ ...c, valueProp: e.target.value })} onBlur={bmc.flush} />
                    <div className="bar"><span className="hint">Parte da tríade, na linguagem do cliente.</span><span className="cnt mono"><b>{c.valueProp.length}</b>/600</span></div>
                  </div>
                  {proposal && (
                    <div className="bmc-sug">
                      <span className="tri-target"><Ic.Sparkle />Sugestão da IA</span>
                      <p>{proposal}</p>
                      <div className="tri-actions">
                        <button type="button" className="btn-primary sm" onClick={() => { bmc.commit({ ...c, valueProp: proposal }); setProposal(null); }}>Usar esta proposta<Ic.Check /></button>
                        <button type="button" className="btn-ghost sm" onClick={() => setProposal(null)}>Manter a minha</button>
                      </div>
                    </div>
                  )}
                </section>
              </div>
              <div className="bmc-col">
                <BmcBlockCard block="segments" canvas={c} onChange={setCanvas} />
                <BmcBlockCard block="relationship" title="Estratégias de relacionamento" canvas={c} onChange={setCanvas} />
                <p className="bmc-tip"><Ic.Link />Ligue cada estratégia de relacionamento aos segmentos que ela atende — o elo em cada item. Passe o mouse para ver as relações.</p>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="bmc-s2">
              <div>
                <div className="bmc-s2-h">
                  <div>
                    <h2>Estruture os demais módulos</h2>
                    <p className="subhead">A IA sugere a partir da tríade, da proposta de valor e dos segmentos — já com as relações. Tudo é editável.</p>
                  </div>
                  <button type="button" className="btn-ghost sm" onClick={() => askBlocks('all')} disabled={suggest.isPending || BMC_REST.every(b => c[b].some(i => i.text.trim()))}>
                    {asking === 'all' ? <span className="spin" /> : <Ic.Sparkle />}Sugerir os que faltam</button>
                </div>
                <div className="bmc-grid6">
                  {BMC_REST.map(b => (
                    <BmcBlockCard key={b} block={b} canvas={c} onChange={setCanvas} busy={asking === 'all' || (Array.isArray(asking) && asking.includes(b))}
                      action={(
                        <button type="button" className="bmc-ask" onClick={() => askBlocks([b])} disabled={suggest.isPending} title={`Sugerir itens para ${BMC_LABEL[b]}`}>
                          {Array.isArray(asking) && asking.includes(b) ? <span className="spin" /> : <Ic.Sparkle />}
                        </button>
                      )} />
                  ))}
                </div>
              </div>
              <aside className="card bmc-base">
                <h3>Base da etapa 1</h3>
                <div className="bmc-base-vp">
                  <span className="lb"><Ic.Star />Proposta de valor</span>
                  <ol className="bmc-tri-mini">
                    {TRIADE_PARTS.map((p, n) => <li key={p.key} className={`p-${p.key}`}><b>{n + 1}</b><span>{cap(triadePiece(p.key, t[p.key])) || '—'}</span></li>)}
                  </ol>
                  <p>{c.valueProp || <em>Sem proposta de valor ainda.</em>}</p>
                </div>
                <div className="bmc-base-b"><span className="lb"><Ic.Target />{BMC_LABEL.segments}</span><ItemChips items={c.segments} /></div>
                <div className="bmc-base-b"><span className="lb"><Ic.Heart />{BMC_LABEL.relationship}</span><ItemChips items={c.relationship} /></div>
                <p className="bmc-tip"><Ic.Sparkle />Mantenha a coerência entre segmentos, canais, fontes de receita e a capacidade de entrega da operação. Passe o mouse sobre um segmento para ver o que está ligado a ele.</p>
              </aside>
            </div>
          )}

          {step === 3 && (
            <div className="bmc-s3">
              <section className="card bmc-canvas-card">
                <div className="gd-h"><h3>Business Model Canvas consolidado</h3><span className="bmc-meta">{bmcMetrics(c).relations} relações · passe o mouse para vê-las</span></div>
                <div className="bmc bmc-live">
                  {(['partners', 'activities', 'resources'] as const).map(b => <Cell key={b} block={b} c={c} />)}
                  <div className="bmc-cell bmc-valueProp">
                    <b><Ic.Star />Proposta de valor</b>
                    <ol className="bmc-tri-mini">
                      {TRIADE_PARTS.map((p, n) => <li key={p.key} className={`p-${p.key}`}><b>{n + 1}</b><span>{cap(triadePiece(p.key, t[p.key])) || '—'}</span></li>)}
                    </ol>
                    <span className="vp">{c.valueProp || 'a preencher'}</span>
                  </div>
                  {(['relationship', 'channels', 'segments', 'costs', 'revenue'] as const).map(b => <Cell key={b} block={b} c={c} />)}
                </div>
              </section>
              <section className="card bmc-read">
                <div className="gd-h"><h3>Leitura consolidada</h3><ReadingStatus updating={updating} basic={q.data?.basic ?? false} at={q.data?.updatedAt ?? null} /></div>
                {!reading ? (
                  updating ? <div className="obj-skel"><i /><i /><i /></div>
                    : <p className="gd-empty">A leitura chega quando o canvas tiver a proposta de valor e ao menos um segmento.</p>
                ) : (
                  <div className={`bmc-read-grid${updating ? ' is-dim' : ''}`}>
                    <div>
                      <div className="bmc-score">
                        <Ring pct={reading.consistencia} />
                        <div><b>Consistência do modelo</b><p>{reading.resumo}</p></div>
                      </div>
                      <ul className="gd-bars bmc-crit">
                        {BMC_CRITERIA.map(([k, label]) => (
                          <li key={k}><span className="nm">{label}</span><span className="tr"><i style={{ '--v': `${reading.criterios[k]}%` } as CSSProperties} /></span><span className="v"><b>{reading.criterios[k]}%</b></span></li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h4><Ic.Sparkle />Leituras da IA</h4>
                      <ol className="bmc-nl">{reading.leituras.map((x, i) => <li key={i}><span className="mono">{i + 1}</span>{x}</li>)}</ol>
                    </div>
                    <div>
                      <h4><Ic.Bulb />Ajustes recomendados</h4>
                      {reading.ajustes.length
                        ? <ol className="bmc-nl">{reading.ajustes.map((x, i) => <li key={i}><span className="mono">{i + 1}</span>{x}</li>)}</ol>
                        : <p className="gd-empty">Nada a ajustar por enquanto.</p>}
                    </div>
                  </div>
                )}
                {q.data?.note && <p className="gd-note"><Ic.Warning />{q.data.note}</p>}
              </section>
            </div>
          )}
        </RelationsProvider>
      )}
      {note && step !== 3 && <p className="gd-note bmc-note"><Ic.Warning />{note}</p>}

      <Actions>
        <button className="btn-ghost" type="button" onClick={() => { flush(); nav('/strategy'); }}>Salvar e sair</button>
        <button className="btn-ghost" type="button" onClick={() => (step && step > 1 ? go((step - 1) as Step) : (flush(), nav('/strategy/valores')))}>Voltar</button>
        <button className="btn-primary" type="button" disabled={consolidate.isPending}
          onClick={() => {
            if (step && step < 3) { go((step + 1) as Step); return; }
            flush(); consolidate.mutate(undefined, { onSettled: () => nav('/strategy/inovacao') });
          }}>
          {consolidate.isPending ? 'Consolidando o DNA…' : step === 3 ? 'Concluir ferramenta' : 'Continuar'}<Ic.ArrowRight />
        </button>
      </Actions>
    </section>
  );
}

function Cell({ block, c }: { block: BmcListBlock; c: BmcContent }) {
  const Icon = BMC_ICON[block];
  return (
    <div className={`bmc-cell bmc-${block}`}>
      <b><Icon />{BMC_LABEL[block]}</b>
      <ItemChips items={c[block]} variant="line" />
    </div>
  );
}

function Ring({ pct }: { pct: number }) {
  const r = 30, len = 2 * Math.PI * r;
  return (
    <div className="bmc-ring">
      <svg viewBox="0 0 72 72" aria-hidden="true">
        <circle cx="36" cy="36" r={r} className="bg" />
        <circle cx="36" cy="36" r={r} className="fg" strokeDasharray={`${(pct / 100) * len} ${len}`} transform="rotate(-90 36 36)" />
      </svg>
      <span><b>{pct}</b><small>/100</small></span>
    </div>
  );
}
